import { DEBUG } from '@src/DEBUG';
DEBUG.open ( '/service/plugin-manager.ts' );

import { Injectable } from '@angular/core';
import { Constants } from '../commons/Constants';


import { BlinkController } from '../commons/plugins/blink-controller';
import { VisualController } from '../commons/plugins/visual-controller';
import { CameraPositionsHandler } from '../commons/plugins/camera-positions-handler';
import { EditorHandler } from '../commons/plugins/editor-handler';
import { FlightpathHandler } from '../commons/plugins/flightpath-handler';
import { FlyAroundHandler } from '../commons/plugins/fly-around-handler';
import { SelectionAreaHandler } from '../commons/plugins/selection-area-handler';
import { BoundingHandler } from '../commons/plugins/bounding-handler';
import { ImagesAlphaController } from '../commons/plugins/images-alpha-controller';
import { ColorizingHandler } from '../commons/plugins/colorizing-handler';
import { PointcloudController } from '../commons/plugins/pointcloud-controller';
import { BoundingSphere } from '../commons/plugins/bounding-sphere';
import { GlobeController } from '../commons/plugins/camera_controllers/globe-controller';
import { PluginController } from '../commons/plugins/plugin-controller';
import { LightController } from '../commons/plugins/camera_controllers/light-controller';
import { CursorController } from '../commons/plugins/camera_controllers/cursor-controller';
import { Images360Controller } from '../commons/plugins/images-360-controller';
import { AppController, FdmApp } from '../commons/plugins/app-controller';
import { LegendHandler } from '../commons/plugins/legend-handler';
import { BucketHandler } from '../commons/plugins/buckets/bucket-handler';
import { TimeController } from '../commons/plugins/camera_controllers/time-controller';
import { PerimeterHandler } from '../commons/plugins/camera_controllers/perimeter-handler';


import { FdmPlugin, FdmPluginInterface } from '../commons/plugins/plugins';
import { XsIdentityInterface } from '../xs/interfaces/identify.interface';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material';
import { MapService } from '../commons/map.service';
import { CoordHelperService } from './coord-helper.service';
import { MeasurementHelperService } from './measurement-helper.service';
import { UiManagerService } from '../xs/services/ui/ui-manager.service';
import { SceneControllerService } from './controllers/scene-controller.service';
import { EntityHelperService } from './entity-helper.service';
import { XsApplicationEventsService } from '../xs/services/events/application-events.service';
import { FeatureDetectorService } from './feature-detector.service';
import { XsColorManagementService } from '../xs/services/gis/color-management.service';
import { LabelsHandler } from '../commons/plugins/labels-handler';
import { BasemapController } from '../commons/plugins/basemap-controller';
import { LiveDataController } from '../commons/plugins/live-data-controller';
import { SceneLiveDataController } from '../commons/plugins/camera_controllers/scene-live-data-controller';

import * as _ from 'lodash'
import { EnvironmentController } from '../commons/plugins/camera_controllers/environment-controller';
@Injectable({
  providedIn: 'root'
})
export class PluginManagerService implements XsIdentityInterface {

  __ID = null;

  debug:boolean = Constants.debug_off;
  debugCounter:number = 0;

  // active plugins
  plugins:any = {};

  // plugin controllers (points of plugin management)
  controllers:any = {};

  // app controller
  appController:any = null;

  // registry of plugins we support
  registry:any = {};
  registryByType:any = { all:[] };
  registryByCardinality:any = { all:[] };
  registryByCategory:any = { all:[] };
  registryByDsType:any = { all:[] };
  registryByTrType:any = { all:[] };
  registryByFeature:any = { all:[] };
  registryByItemId:any = {};

  registryGuid:any = {};

  // our plugin prototypes
  protos:any = {};

  // our incoming and outgoing injectors
  // static injectors = {}; disabled; moved injection to FdmPlugin using FourDInjector themselves

  // cached lists of plugins
  listOf:any = {
    active: {},
    shortcuts: {},
  };

  // currently wanted plugin categories
  pluginCategories:string[] = [];

  cesiumHelper:any = null;

  constructor ( 
    public translate:TranslateService, 
    public mapService:MapService, 
    public coordHelper: CoordHelperService, 
    public entityHelper:EntityHelperService,
    public dialog: MatDialog, 
    public measureHelper: MeasurementHelperService, 
    public uiManager: UiManagerService, 
    public sceneController:SceneControllerService,
    public applicationEvents: XsApplicationEventsService,
    public featureDetector: FeatureDetectorService,
    public colorManager: XsColorManagementService
  ) {

    DEBUG.log ( '/service/plugin-manager.service.ts' );

    this.__ID = XsIdentityInterface.__init(this);

    if ( this.debug ) { console.log ( "%c pluginManager CONSTRUCTOR()", Constants._yellow, this.debug ); }

    // we support dealing with these plugins
    this.registerPlugins ([ 
      VisualController, SelectionAreaHandler, BoundingHandler, CameraPositionsHandler, EditorHandler, FlightpathHandler, FlyAroundHandler,
      ImagesAlphaController, BlinkController, ColorizingHandler, PointcloudController, BoundingSphere, GlobeController, LightController, CursorController, Images360Controller,
      LegendHandler, LabelsHandler, BucketHandler, TimeController, PerimeterHandler, BasemapController, LiveDataController, SceneLiveDataController, EnvironmentController
    ]);

    this.startupApplications();

  }

  registerGuidObject ( item:any ) {

    let guid = null;
    
    if ( item ) {
      if ( ! item['__guid'] ) {
        item['__guid'] = this.uuidv4();
      }
      guid = item['__guid'];

      if ( ! this.registryGuid[guid] ) {
        this.registryGuid[guid] = [];
      }
    }

    // console.log ('PLUGIN_MNG :: REGISTER_GUID', item, guid );
    return guid;
  }

  registerPluginToGuidObject ( item:any, id:any ) {

    let guid = item ? item['__guid'] : null;

    if ( !guid ) {
      return false;
    }

    this.registryGuid[guid].push(id);
    // console.log ('PLUGIN_MNG :: REGISTER_PLUGIN_GUID', item, id );
  }

  unregisterPluginFromGuid ( guid:any, id:any ) {
    if ( this.registryGuid[guid] ) {
      // console.log ('PLUGIN_MNG :: UNREGISTER_PLUGIN', guid, id );
      this.registryGuid[guid] = this.registryGuid[guid].filter( g => g !== id );
    }
  }

  unregisterGuid ( guid:any ) {
    if ( this.registryGuid[guid] ) {
      this.registryGuid[guid].forEach(id => {
        /* if ( this.plugins[id] && this.plugins[id].controller ) {
          this.plugins[id].controller.remove( id );
        } else if ( this.plugins[id] ) {
          this.delete(id);
        } */
        this.unregisterPluginFromGuid ( guid, id );
      });
    }
  }

  tellGuidPlugins ( item:any, action:string = 'update', data:any = null ) {

    let guid = item['__guid'];

    // console.log ('PLUGIN_MNG :: TELL_PLUGINS', guid, this.registryGuid );

    if ( this.registryGuid[guid] ) {
      this.registryGuid[guid].forEach(id => {
        // console.log ('PLUGIN_MNG :: TELL_PLUGIN', id, this.plugins[id] );
        if ( this.plugins[id] ) {
          this.plugins[id].tell( action, data );
        }
      });
    }

  }

  uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
  }

  registerPlugins ( list:any[] ) {
    list.forEach ( p => this.registerPlugin(p) );
    if ( this.debug ) { console.log ( '@@@@@@@@@@@@@@@@@@@@@@@@@ register plugins', 
    { DsType:this.registryByDsType, TrType:this.registryByTrType, Feature:this.registryByFeature, Type:this.registryByType } ); }
  }

  registerPlugin ( who:any ) {
    if ( who.meta ) {

      let meta = _.clone(who.meta);
      let name = meta.name;
      this.protos[name] = who;
      this.registry[name] = meta;

      if ( meta.cardinality ) {
        let idx = meta.cardinality;
        if ( ! this.registryByCardinality[idx] ) {
          this.registryByCardinality[idx] = [];
        }
        this.registryByCardinality[idx].push ( name );
      }

      // category
      if ( ! meta.category ) {
        meta.category = FdmPlugin.category.generic;
      }
      let idx = meta.category;
      if ( ! this.registryByCategory[idx] ) {
        this.registryByCategory[idx] = [];
      }
      this.registryByCategory[idx].push ( name );


      // dataset type
      if ( ! meta.ds_types || meta.ds_types.length < 1 ) {
        meta.ds_types = [ FdmPlugin.ds_types.all ];
      }
      meta.ds_types.forEach( t => {
        if ( ! this.registryByDsType[t] ) {
          this.registryByDsType[t] = [];
        }
        this.registryByDsType[t].push ( name );
      });


      // transform type
      if ( ! meta.tr_types || meta.tr_types.length < 1 ) {
        meta.tr_types = [ FdmPlugin.tr_types.all ];
      }
      meta.tr_types.forEach( t => {
        if ( ! this.registryByTrType[t] ) {
          this.registryByTrType[t] = [];
        }
        this.registryByTrType[t].push ( name );
      });
      

      // plugin types
      if ( ! meta.types || meta.types.length < 1 ) {
        meta.types = [ FdmPlugin.types.allController ];
      }
      meta.types.forEach( t => {
        if ( ! this.registryByType[t] ) {
          this.registryByType[t] = [];
        }
        this.registryByType[t].push ( name );
      });


      // features
      if ( ! meta.features || meta.features.length < 1 ) {
        meta.features = [ FdmPlugin.features.all ];
      }
      meta.features.forEach( t => {
        if ( ! this.registryByFeature[t] ) {
          this.registryByFeature[t] = [];
        }
        this.registryByFeature[t].push ( name );
      });


      // icon
      if ( ! meta['icon'] ) {
        meta.icon = 'cog';
      }

      // tooltip
      if ( ! meta['tooltip'] ) {
        meta.tooltip = 'NONE';
      }

      // console.log('##REG_PLUGIN', name, meta );

    }
  }

  getLabelById ( id:string ) {
    if ( this.plugins[id] ) {
      return this.plugins[id].meta.label;
    }
    return id + ' | ' + this.plugins[id];
  }

  getLabelByName ( name:string ) {
    if ( this.protos[name] ) {
      return this.protos[name].meta.label;
    }
    return name + ' | ' + this.protos[name];
  }

  _check ( id:string, value:any, additional:any[] = null ) {
    let res = {};
    let ret = [];
    let these = [];
    let me = this;
    if ( ! value['length'] ) {
      these = [ value ];
    } else {
      these = value;
    }
    if ( additional ) {
      additional.forEach(a => {
        these.push(a);
      });
    }
    // console.log('! check', id, these );
    if ( these && these.length > 0 ) {
      these.forEach ( val => {
        
        if ( me[id] ) {
          if ( me[id][val] ) {
            // console.log('! check found', val, me[id][val] );
            me[id][val].forEach ( item => {
              res[item] = val;
            });
          }
        }
      });
      ret = Object.keys(res);
    }
    // console.log('! check FINAL', ret);
    return ret;
  }

  listByCategory ( type:string|string[], id:string = null ) {
    return this._check ( 'registryByCategory', type );
  }

  listByType ( type:string|string[], id:string = null ) {
    return this._check ( 'registryByType', type );
  }

  listByFeature ( type:string|string[], id:string = null ) {
    return this._check ( 'registryByFeature', type );
  }

  listByDsType ( type:string|string[], id:string = null ) {
    return this._check ( 'registryByDsType', type, [ FdmPlugin.ds_types.all ] );
  }

  listByTrType ( type:string|string[], id:string = null ) {
    return this._check ( 'registryByTrType', type, [ FdmPlugin.tr_types.all ] );
  }

  filterFromConfig ( id:string, pool:any, config:any ) {
    if ( config[id] && config[id].length > 0 ) {
      let types = null;
      switch ( id ) {
        case 'filterByType': 
          types = this.listByType ( config.filterByType ); break;
        case 'filterByItemDsType': 
        case 'filterByDsType': 
          types = this.listByDsType ( config.filterByDsType ); break;
        case 'filterByItemTrType': 
        case 'filterByTrType': 
          types = this.listByTrType ( config.filterByTrType ); break;
        case 'filterByFeature': 
          types = this.listByFeature ( config.filterByFeature ); break;
      }
      if ( types ) {
        // pool = pool.filter( function (e) { return this.indexOf(e) >= 0 }, types );
        let newPool = types.filter(function(e) {
          return pool.indexOf(e) > -1;
        });
        // console.log ( '%c ***** pool after ['+id+']', Constants._orange, pool, types, newPool );
        pool = _.clone(newPool);
      }
    }
    return pool;
  }

  getTotalPlugins ( config:any = {}, item:any = null ) {

    // console.log ( '%c getTotalPlugins() config', Constants._red, config, item );

    let pool = this.listByCategory ( config.categories );
    // console.log ( '%c ***** pool', Constants._orange, pool, config.categories );

    pool = this.filterFromConfig ( 'filterByType', pool, config );   
    // console.log ( '%c ** getTotalPlugins() filterByType', Constants._red, pool );
    
    pool = this.filterFromConfig ( 'filterByDsType', pool, config );
    // console.log ( '%c ** getTotalPlugins() filterByDsType', Constants._red, pool );

    pool = this.filterFromConfig ( 'filterByTrType', pool, config );
    // console.log ( '%c ** getTotalPlugins() filterByTrType', Constants._red, pool );

    pool = this.filterFromConfig ( 'filterFeature', pool, config );
    // console.log ( '%c ** getTotalPlugins() filterFeature', Constants._red, pool );


    if ( item && config.filterByItemDsType ) {
      let type = item['ds_type_uid'];
      pool = this.filterFromConfig ( 'filterByItemDsType', pool, { filterByItemDsType: [ type ], filterByDsType: [ type ] } );
      // console.log ( '%c ** getTotalPlugins() filterByItemDsType', Constants._red, type, pool );
    }

    if ( item && config.filterByItemTrType ) {
      let type = item['type_uid'];
      pool = this.filterFromConfig ( 'filterByItemTrType', pool, { filterByItemTrType: [ type ], filterByTrType: [ type ] } );
      // console.log ( '%c ** getTotalPlugins() filterByItemTrType', Constants._red, type, pool );
    }

    // console.log ( '%c ** getTotalPlugins() ** DONE', Constants._red, config, item, pool );

    return pool;
  }

  getActivePlugins ( config:any = {}, item:any = null ) {
    let ret = [];
    if ( item ) {
      let itemId = config.createId ( item );
      Object.keys(this.plugins).forEach(key => {
        let plug = this.plugins[key];
        // console.log('@@', itemId, plug );
        if ( plug.itemId && plug.itemId == itemId ) {
          ret.push(plug.id);
        }
      });
    }
    // console.log ( '%c getActivePlugins() config', Constants._red, item.id, config, item, ret );
    return ret;
  }

  getAvailablePlugins ( config:any = {}, item:any = null, total:any = null, active:any = null ) {
    let ret = [];
    if ( total ) {
      if ( active && active.length > 0 ) {
        total.forEach( name => {

          let find = active.find( pluginId => {
            let p = this.get(pluginId);
            return p ? p.plugin_name == name : false;
          });
          if ( !find ) {
            ret.push(name);
          }
        });    
      } else {
        ret = total;
      }
    }
    // console.log ( '%c getAvailablePlugins() config', Constants._red, config );
    // console.log ( '%c getAvailablePlugins() item', Constants._red, item );
    // console.log ( '%c getAvailablePlugins() total,active,ret', Constants._red, total, active, ret );
    return ret;
  }

  getShortcutPlugins ( config:any = {}, item:any = null, available:any = null, active:any = null ) {
    let ret = [];
    // console.log('## available', available );
    if ( available && available.length > 0 ) {
      available.forEach(pluginName => {
        // console.log('## available', pluginName, this.registry[pluginName] );
        if ( this.registry[pluginName] && this.registry[pluginName]['shortcut'] ) {
          ret.push({ name:pluginName, specifiedShortcut:true, active:false, show:true, icon: this.registry[pluginName]['icon'] ? this.registry[pluginName]['icon'] : 'gear', tooltip: this.registry[pluginName]['tooltip'], label: this.registry[pluginName]['label'], translator: this.registry[pluginName]['translator'] });
        } else if ( this.registry[pluginName] && this.registry[pluginName]['activeShortcut'] ) {
          ret.push({ name:pluginName, specifiedShortcut:true, active:false, show:false, icon: this.registry[pluginName]['icon'] ? this.registry[pluginName]['icon'] : 'gear', tooltip: this.registry[pluginName]['tooltip'], label: this.registry[pluginName]['label'], translator: this.registry[pluginName]['translator'] });
        } else if ( this.registry[pluginName] && ! ( this.registry[pluginName]['shortcut'] || this.registry[pluginName]['activeShortcut'] ) ) {
          ret.push({ name:pluginName, specifiedShortcut:false, active:false, show:false, icon: this.registry[pluginName]['icon'] ? this.registry[pluginName]['icon'] : 'gear', tooltip: this.registry[pluginName]['tooltip'], label: this.registry[pluginName]['label'], translator: this.registry[pluginName]['translator'] });
        }
      });
    }
    if ( active && active.length > 0 ) {
      
      // console.log('## active', active, this.pluginCategories );
        
      active.forEach(pluginId => {
        let p = this.get(pluginId);
          
        if ( p ) {
          let c = p.meta['category'];
          let pluginName = p.plugin_name;
          let alsoAvailable = this.pluginCategories.find( a => a == c );
          // if ( this.registryByCategory)
          // console.log('## active CATEGORY', c, pluginName, alsoAvailable, p, this.registryByCategory[c] );
          if ( alsoAvailable && ( p.meta['shortcut'] || p.meta['activeShortcut'] ) ) {
            ret.push({ name:pluginName, id:pluginId, specifiedShortcut:true, active:true, show:true, icon: p.meta['icon'] ? p.meta['icon'] : 'gear', tooltip: p.meta['tooltip'], label: p.meta['label'], translator: this.registry[pluginName]['translator'] });
          } else if ( !p.meta['shortcut'] && !p.meta['activeShortcut'] ) {
            ret.push({ name:pluginName, id:pluginId, specifiedShortcut:false, active:true, show:false, icon: p.meta['icon'] ? p.meta['icon'] : 'gear', tooltip: p.meta['tooltip'], label: p.meta['label'], translator: this.registry[pluginName]['translator'] });
          }
        }
      });
    }
    ret.sort();
    return ret;
  }

  createProto ( pluginControllerName:string ) {

    let nue = null;

    // this.debug = true;

    if ( this.debug ) { console.log ( 'pluginManager.createProto()', pluginControllerName ); }

    // make sure that Cesium helper always available
    if ( this.mapService && this.mapService.helper ) {
      this.cesiumHelper =  this.mapService.helper;
    }

    if ( this.protos[pluginControllerName] ) {
      nue = new this.protos[pluginControllerName](this);
      if ( nue.meta ) {
        if ( ! nue.meta['category'] ) {
          nue.meta.category = FdmPlugin.category.generic;
        }
        if ( ! nue.meta['features'] ) {
          nue.meta.features = FdmPlugin.features.all;
        }
        if ( ! nue.meta['types'] ) {
          nue.meta.types = FdmPlugin.types.allController;
        }
        if ( ! nue.meta['ds_types'] ) {
          nue.meta.ds_types = FdmPlugin.ds_types.all;
        }
        if ( ! nue.meta['tr_types'] ) {
          nue.meta.tr_types = FdmPlugin.tr_types.all;
        }
      }
    }

    this.debug = false;

    return nue;
  }

  create ( pluginControllerName:string, item:any = null ) {
    
    // this.debug = true;

    if ( this.debug ) { console.log ( 'pluginManager.create()', pluginControllerName, item ); }

    let n = this.createProto(pluginControllerName);
    
    if ( n && item ) {
      n.itemId = item.id;
      n.injectItem(item);
    }

    // console.log('pluginManager :: create('+pluginId+')', n );

    if ( this.set ( n.id, n ) ) {
      this.debug = false;
      return n.id;
    };
    this.debug = false;
    return null;
  }

  get ( id:string ) {
    if ( this.plugins[id] ) {
      return this.plugins[id];
    }
    return null;
  }

  set ( id:string, any:any ) {
    if ( ! this.plugins[id] ) {
      this.plugins[id] = any;
      return true;
    }
    return false;
  }

  check ( id:string ) {
    if ( this.plugins[id] ) {
      return true;
    }
    return false;
  }

  remove ( id:string ) {
    if ( this.debug ) { console.log ( 'pluginManager.remove()', id ); }
    this.delete ( id, true );
  }

  delete ( id:string, butDontDeleteSavedInfo:boolean = false ) {
    // this.debug = true;
    if ( this.debug ) { console.log ( 'pluginManager.delete() START', id, butDontDeleteSavedInfo, this.plugins[id] ); }
    if ( this.plugins[id] ) {

      const plugin = this.plugins[id];

      if ( plugin.item && butDontDeleteSavedInfo ) {
        plugin.saveToItem();
      }

      if ( plugin.item && ! butDontDeleteSavedInfo ) {
        plugin.deleteFromItem();
      }

      plugin.destroy();

      if ( plugin.item ) {
        if ( this.debug ) { console.log ( 'pluginManager.delete() ITEM' ); }
        plugin.item = null;
        delete plugin.item;
      }

      if ( plugin.object ) {
        if ( this.debug ) { console.log ( 'pluginManager.delete() OBJECT' ); }
        plugin.object = null;
        delete plugin.object;
      }

      if ( plugin.controller ) {
        if ( this.debug ) { console.log ( 'pluginManager.delete() CONTROLLER' ); }
        plugin.controller = null;
        delete plugin.controller;
      }

      if ( plugin.plugs && Object.keys(plugin.plugs).length ) {
        if ( this.debug ) { console.log ( 'pluginManager.delete() PLUGS' ); }
        Object.keys(plugin.plugs).forEach ( key => {
          let p = plugin.getPlug(key);
          if ( p ) {
            if ( this.debug ) { console.log ( 'pluginManager.delete() PLUG', key ); }
            if ( p.object ) {
              p.object.destroy();
              delete p.object;
            }
            p = null;
          }
        });
      }
      
      delete this.plugins[id];
    }
    if ( this.debug ) { console.log ( 'pluginManager.delete() END', id, butDontDeleteSavedInfo, this.plugins[id] ); }
    this.debug = false;
    return this.plugins[id];
  }

  mergeUniqueArrays(arr1, arr2){
    return arr1.concat(arr2.filter(function (item) {
        return arr1.indexOf(item) === -1;
    }));
  }


  findPlugin ( pluginName:string, controllerName:string = null ) {
    let ret = null;
    if ( !this.controllers ) {
      return ret;
    }
    if ( controllerName ) {
      if ( this.controllers[controllerName] ) {
        ret = this.controllers[controllerName].getByName(pluginName);
      }
    } else {
      Object.keys(this.controllers).forEach(key => {
        if ( !ret ) {
          const controller = this.controllers[key];
          if ( controller ) {
            let test = controller.getByName(pluginName);
            if ( test ) {
              ret = test;
            }
          }
        }
      });
    }
    return ret;
  }

  createController ( id:string, config:any = {} ) {
    config['_token'] = id;
    const new_controller = new PluginController( id, config, this );
    this.setController ( id, new_controller );
    new_controller.updateCategories(this.pluginCategories);
    return new_controller;
  }

  createAppController ( id:string, config:any = {} ) {
    const new_controller = new AppController( id, config );
    new_controller.updateCategories(this.pluginCategories);
    return new_controller;
  }

  getController ( id:string ) {
    if ( this.controllers[id] ) {
      return this.controllers[id];
    }
    return null;
  }

  setController ( id:string, any:any ) {
    if ( ! this.controllers[id] ) {
      this.controllers[id] = any;
      return true;
    }
    return false;
  }

  checkController ( id:string ) {
    if ( this.controllers[id] ) {
      return true;
    }
    return false;
  }

  deleteController ( id:string ) {
    if ( this.controllers[id] ) {
      this.controllers[id].destroy();
      delete this.controllers[id];
    }
  }

  clearCacheAllControllers() {
    // console.log('clear all cache');
    Object.keys(this.controllers).forEach(key => {
      let controller = this.controllers[key];
      if ( controller ) {
        controller.clearCache();
        controller.updateCategories(this.pluginCategories);
      }
    });
  }

  startupApplications() {
        
    // create a controller for global Apps
    this.appController = this.createAppController ( 'apps', {
      label: 'App controller',
    });

    let d = this.appController.create ( 'debug', {

      label:'Tasker',
      category: 'admin',
      icon: 'tasks',
      
      products:[
        { id:'', }
      ],

      states: {
        setup:{
          content:[
            { type:'plain', name:'Some text' },
            { type:'button', value:'Create new app', action:'startProcess', process:'fdm_create_new_app' },
            { type:'button', value:'Start steps', action:'startProcess', process:'my_steps' }
          ],
          footer:[
            { type:'button', value:'Start steps', action:'startProcess', process:'my_steps' }
          ]
        },
        queued:{
          content:[
            { type:'plain', name:'queing' },
          ],
        },
        processing:{
          content:[
            { type:'plain', name:'processing' },
          ],
        },
        succeeded:{
          content:[
            { type:'plain', name:'Hurrah!' },
          ],
        }
      },

      processes: [
        {
          uid:'my_steps',
          label:'Step by step process',
          type:'steps',
          icon:'home',

          action: {
            onSucceeded: [
              { action:'processEnd' }
            ]
          },

          processes: [
            { label:'First' },
            { label:'Second' },
            { label:'Final', panels: { succeeded: {content:[ { type:'process', action:'endProcess' } ] } } }
          ],
        },
        { import:'fdm_create_new_app' },
        { import:'fdm_load_app' },
      ],

      menus:[
        { id:'file', label:'File', items:[
          { id:'new', label:'New', process:'new-item' },
          { id:'open', label:'Open', process:'open-item' },
          { id:'exit', label:'Exit', function:'close-app' },
        ] },
        { id:'test', label:'Test', items:[
          { id:'d44', label:'Blahdi' }
        ] },
      ],

    });

    this.appController.create ( 'make-it-accurate', {

      label:'Make it accurate',
      icon:'bullseye',

      states: {
        setup:{
          content:[
            { type:'panel', id:'history', },
            { type:'file-dropper' },
            { type:'button', value:'Create new app', action:'startProcess', process:'fdm_create_new_app' },
            { type:'button', value:'Start steps', action:'startProcess', process:'my_steps' }
          ],
        },
      },

      

    });


/*    
    // default 'open', 'queued','processing','failed','aborted','success'
    //             start     process    fail     abort     done

    let a = {
      id:'fdm-get-photobucket',
      // states: [ 'open', 'processing', 'success' ], -- states are default 'open', 'queued','processing','failed','aborted','success'
      state: 'open',

      products:{
        photos: { type:'list', dataType:'Photo', process:'fdm-get-photobucket' },
        job:    { type:'job', process:'fdm-get-job', config:{ jobId:'jobId', jobType:'jobType' } },
        menu:   { type:'list', dataType:'integer:string' }
      },

      steps: [
        { step:0, label:'Select photobucket', states:[
          { id:'open', items:[
            { type:'condition', if:'count', param:'photobucket', moreThan:7, goto:'processing' },
            { type:'text', value:'Not enough images in the bucket. Want to upload?' },
            { type:'button', label:'Yes', clickById:'guide-click-upload' },
            { type:'button', label:'No', action:'cancel' }
          ] },
          { id:'processing', items:[
            { type:'action', do:'getTransforms', param:'photobucket', intoProduct:'photos' }
          ] },
          { id:'success', items:[
            { type:'action', result:'product', function:'get', param:'photobucket', state:'success' }
          ] }
        ] }
      ]
    };

    this.appController.create ( 'test1', {

      label:'Test app 1',

      menus:[
        { id:'file', label:'File', items:[
          { id:'new', label:'New', process:'new-item' },
          { id:'open', label:'Open', process:'open-item' },
          { id:'exit', label:'Exit', function:'close-app' },
        ] },
        { id:'test', label:'Test', items:[
          { id:'d44', label:'Blahdi' }
        ] },
      ],

      processes: [
        { id:'new-item1' },
        { id:'new-item2' },
        { id:'new-item3' }
      ]

    });

    this.appController.create ( 'test2', {

      label:'Test app 2',

      config: {
        showCloseIcon: true,
      },

      menus:[
        { id:'file', label:'File', items:[
          { id:'new', label:'New', process:'new-item' },
          { id:'open', label:'Open', process:'open-item' },
          { id:'exit', label:'Exit', function:'close-app' },
        ] },
        { id:'test', label:'Test', items:[
          { id:'d44', label:'Blahdi' }
        ] },
      ],

      processes: [
        { id:'new-item' }
      ]

    });
*/
  }
}
